/**
 * Cart Attributes Updater
 * Automatically updates cart attributes with item details (variant ID, title, options, preorder status)
 * Format is optimized for easy parsing in Shopify email templates
 */
class CartAttributesUpdater {
    constructor() {
        this.init();
    }

    /**
     * Initialize event listeners
     */
    init() {
        // Update attributes when cart is loaded. Visitors without a cart
        // cookie have nothing to sync yet, so skip the page-load pass for them:
        // it used to cost every visitor two GET /cart.js and a POST
        // /cart/update.js on every page view.
        if (document.cookie.indexOf("cart=") !== -1) {
            if (document.readyState === "loading") {
                document.addEventListener(
                    "DOMContentLoaded",
                    () => {
                        this.updateCartAttributes();
                    },
                );
            } else {
                this.updateCartAttributes();
            }
        }

        // Update attributes when cart changes
        document.addEventListener("cart:updated", () => {
            this.updateCartAttributes();
        });

        document.addEventListener("cart:refresh", () => {
            this.updateCartAttributes();
        });

        document.addEventListener(
            "line-item:change:end",
            () => {
                this.updateCartAttributes();
            },
        );

        // Update attributes when cart drawer is opened
        // This ensures attributes are always up-to-date even if user just opens the drawer
        document.addEventListener(
            "cart-drawer:open",
            () => {
                this.updateCartAttributes();
            },
        );
    }

    /**
     * Get current cart data
     */
    async getCart() {
        try {
            const response = await fetch("/cart.js");
            return await response.json();
        } catch (error) {
            console.error("Error fetching cart:", error);
            return null;
        }
    }

    /**
     * Check if item is preorder based on inventory and brand rules
     */
    async isPreorderItem(item) {
        // Check if CartPreorderChecker exists and has the logic
        if (window.CartPreorderChecker) {
            const checker = window.CartPreorderChecker;
            const variantId = checker.convertToGid(
                item.variant_id,
            );

            // Try to get inventory data from cache first
            let inventoryData =
                checker.inventoryCache?.get(variantId);

            // If not in cache, fetch it
            if (!inventoryData) {
                try {
                    inventoryData =
                        await checker.fetchVariantInventory(
                            variantId,
                        );
                    // Cache it
                    if (inventoryData) {
                        if (!checker.inventoryCache) {
                            checker.inventoryCache =
                                new Map();
                        }
                        checker.inventoryCache.set(
                            variantId,
                            inventoryData,
                        );
                        checker.checkedVariants.add(
                            variantId,
                        );
                    }
                } catch (error) {
                    console.error(
                        "Error fetching inventory data:",
                        error,
                    );
                }
            }

            // Add tags to item if available
            if (inventoryData?.product?.tags) {
                item.tags = inventoryData.product.tags;
            }

            // Now check by name and tags (Essentials, Stussy, Adidas Tang)
            const shouldShowByName =
                checker.shouldShowPreorderByName(item);

            // If matched by name and has streetwear tag, it's preorder
            if (shouldShowByName) {
                return true;
            }

            // Otherwise check inventory
            // Note: quantityAvailable can be null if inventory tracking is disabled,
            // in that case we should NOT treat it as preorder
            if (
                inventoryData &&
                inventoryData.quantityAvailable !== null &&
                inventoryData.quantityAvailable <= 0
            ) {
                return true;
            }
        }

        return false;
    }

    /**
     * Update cart attributes with items data
     */
    async updateCartAttributes() {
        try {
            const cart = await this.getCart();

            if (!cart) {
                return;
            }

            // IMPORTANT: Clear ALL existing item attributes first
            // This prevents leftover attributes from deleted items
            const attributes =
                this.clearAllItemAttributes(cart);

            // Empty cart with nothing to clear: no request needed.
            if (
                cart.items.length === 0 &&
                Object.keys(attributes).length === 0
            ) {
                return;
            }

            // Store total item count
            attributes._items_count = cart.items.length;

            // Add minimal attributes for each item (only what's needed for email templates)
            // Format: _item_X_variant_id, _item_X_is_preorder
            // Email templates already have access to line_item.title, line_item.quantity, etc.
            // IMPORTANT: Use for loop instead of forEach to properly await async calls
            for (
                let index = 0;
                index < cart.items.length;
                index++
            ) {
                const item = cart.items[index];
                const itemNum = index + 1;
                const isPreorder =
                    await this.isPreorderItem(item);

                // Only store variant_id (for matching) and preorder status (for badge display)
                attributes[`_item_${itemNum}_variant_id`] =
                    item.variant_id;
                attributes[`_item_${itemNum}_is_preorder`] =
                    isPreorder ? "yes" : "no";
            }

            // Update cart with attributes
            await fetch("/cart/update.js", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    attributes: attributes,
                }),
            });
        } catch (error) {
            console.error(
                "Error updating cart attributes:",
                error,
            );
        }
    }

    /**
     * Clear all existing item attributes to prevent leftovers from deleted items
     * Returns an object with all old attributes set to empty string for removal
     */
    clearAllItemAttributes(cart) {
        const clearedAttributes = {};

        if (!cart || !cart.attributes) {
            return clearedAttributes;
        }

        // Find all existing _item_X_* attributes and set them to empty string to remove
        for (const key in cart.attributes) {
            if (key.startsWith("_item_")) {
                clearedAttributes[key] = "";
            }
            // Also clear old _items_data if it exists
            if (key === "_items_data") {
                clearedAttributes[key] = "";
            }
        }

        return clearedAttributes;
    }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
        window.CartAttributesUpdater =
            new CartAttributesUpdater();
    });
} else {
    window.CartAttributesUpdater =
        new CartAttributesUpdater();
}
